import { CoachSmartCatalogPayload, SmartCatalogProduct, SmartCatalogTheme } from "@/services/walter-assistant";

/**
 * Fixtures statiques pour itérer visuellement sur le Smart Catalog sans
 * brancher le backend. Activables via le hash `#smart-catalog-mock=success`
 * (ou `degraded` / `empty`) en dev — voir `coach-panel.tsx`.
 *
 * Les fixtures ne doivent JAMAIS être importées en prod : le bundler
 * peut les laisser dans le bundle (tree-shaking conservateur), donc on
 * les garde derrière un check `import.meta.env.DEV` côté appelant.
 */

const NOW = Date.UTC(2026, 4, 27, 14, 30, 0); // wall-clock fixe pour les snapshots
const DAYS = 1000 * 60 * 60 * 24;

const VERBATIM_READY = {
    transition_sentence: "Comme vous êtes infirmier libéral, je pense que cette formation va vous parler.",
    bullet_1: "Reconnue DPC, prise en charge complète pour les libéraux.",
    bullet_2: "Cas pratiques tirés du terrain, pas de blabla théorique.",
    verbatim_status: "ready" as const,
};

const VERBATIM_PENDING = {
    transition_sentence: "",
    bullet_1: "",
    bullet_2: "",
    verbatim_status: "pending" as const,
};

function product(overrides: Partial<SmartCatalogProduct>): SmartCatalogProduct {
    return {
        product_code: "BASE-001",
        name: "Produit générique",
        modality: "synchrone",
        duration_hours: 14,
        listing_price: 1490,
        funding_price: 1490,
        personal_funding: { type: "rac", montant: 0, exact: true },
        eligible_fundings: [{ code: "DPC", label: "DPC", type: "dpc" }],
        score: 1.0,
        is_top_pick: false,
        next_sessions: [
            {
                starts_at: new Date(NOW + 14 * DAYS).toISOString(),
                ends_at: new Date(NOW + 14 * DAYS + 8 * 60 * 60 * 1000).toISOString(),
                capacity: 12,
                booked_seats: 8,
                seats_left: 4,
                modality: "synchrone"
            }
        ],
        ...overrides
    };
}

function theme(overrides: Partial<SmartCatalogTheme>): SmartCatalogTheme {
    return {
        theme_code: "theme-generic",
        theme_title: "Thème générique",
        domain: "sante",
        sub_table: "sante",
        score: 1.0,
        selling_point: "",
        catchphrase: "",
        verbatim: { ...VERBATIM_READY },
        products: [],
        ...overrides
    };
}

/** Thème santé "douleur" avec 6 produits (top 4 + 2 grisés), thèmes généralistes. */
export const mockSuccessPayload: CoachSmartCatalogPayload = {
    type: "coach.smart_catalog",
    group_id: "mock-group-success",
    catalog_origins: ["sante"],
    trigger_reason: "signal_change",
    status: "success",
    generated_at_ms: NOW - 15 * 1000,
    themes: [
        theme({
            theme_code: "douleur_soins_palliatifs",
            theme_title: "Douleur & soins palliatifs",
            domain: "sante",
            sub_table: "sante",
            score: 1.0,
            verbatim: {
                transition_sentence: "Vous m'avez dit que vous voyiez beaucoup de patients en fin de vie ?",
                bullet_1: "Des formations cliniques validées DPC, en petit groupe.",
                bullet_2: "Cas pratiques tirés du terrain, pas de blabla théorique.",
                verbatim_status: "ready",
            },
            products: [
                product({
                    product_code: "SP-DPC-014",
                    name: "Prise en charge de la douleur",
                    modality: "synchrone",
                    duration_hours: 21,
                    listing_price: 1690,
                    funding_price: 1690,
                    personal_funding: { type: "rac", montant: 0, exact: true },
                    is_top_pick: true,
                    score: 1.0,
                    next_sessions: [
                        {
                            starts_at: new Date(NOW + 10 * DAYS).toISOString(),
                            ends_at: new Date(NOW + 10 * DAYS + 8 * 60 * 60 * 1000).toISOString(),
                            capacity: 12,
                            booked_seats: 11,
                            seats_left: 1,
                            modality: "synchrone"
                        }
                    ],
                }),
                product({ product_code: "SP-HYB-003", name: "Éducation thérapeutique du patient diabétique", modality: "hybride", duration_hours: 28, listing_price: 1990, funding_price: null, personal_funding: { type: "rac", montant: null, montant_min: 190, montant_max: 590, exact: false }, eligible_fundings: [{ code: "OPCO_EP", label: "OPCO EP", type: "opco" }], score: 0.86, next_sessions: [] }),
                product({ product_code: "SP-CPF-008", name: "Plaies et cicatrisations complexes", modality: "asynchrone", duration_hours: 18, listing_price: 990, funding_price: null, personal_funding: { type: "rac", montant: 990, exact: true }, eligible_fundings: [{ code: "FIF-PL", label: "FIF-PL", type: "fifpl" }], score: 0.74, next_sessions: [] }),
                product({ product_code: "SP-DPC-021", name: "Vaccinations en officine et au cabinet", modality: "synchrone", duration_hours: 14, listing_price: 1290, funding_price: 1170, personal_funding: { type: "rac", montant: 120, exact: true }, score: 0.62, next_sessions: [] }),
                product({ product_code: "SP-ASY-019", name: "Gestes d'urgence — recyclage AFGSU 2", modality: "asynchrone", duration_hours: 7, listing_price: 1490, funding_price: null, personal_funding: { type: "fp", montant: 1490, exact: true }, eligible_fundings: [{ code: "ALMA", label: "ALMA", type: "alma" }], score: 0.41, next_sessions: [] }),
                product({ product_code: "SP-ASY-044", name: "Nutrition de la personne âgée", modality: "asynchrone", duration_hours: 10, listing_price: 490, funding_price: null, personal_funding: null, eligible_fundings: [], score: 0.33, next_sessions: [] }),
            ]
        }),
        theme({
            theme_code: "langues",
            theme_title: "Langues vivantes",
            domain: "generaliste",
            sub_table: "generaliste",
            score: 0.82,
            verbatim: {
                transition_sentence: "Vous me parliez d'un projet à l'international ?",
                bullet_1: "Certifié BRIGHT, finançable 100% CPF.",
                bullet_2: "Coach dédié, sessions 1-to-1 hebdomadaires.",
                verbatim_status: "ready",
            },
            products: [
                product({ product_code: "G-CPF-101", name: "Anglais professionnel — niveau B2", modality: "asynchrone", duration_hours: 40, listing_price: 1490, funding_price: 1490, personal_funding: { type: "rac", montant: 0, exact: true }, eligible_fundings: [{ code: "CPF", label: "CPF", type: "cpf" }], is_top_pick: true, score: 0.82, next_sessions: [] }),
            ]
        }),
        theme({
            theme_code: "bureautique",
            theme_title: "Bureautique",
            domain: "generaliste",
            sub_table: "generaliste",
            score: 0.6,
            verbatim: {
                transition_sentence: "Vous bricolez sur Excel mais sans vraiment maîtriser ?",
                bullet_1: "TOSA certifié, valorisé sur les fiches de poste.",
                bullet_2: "Format e-learning + ateliers cas réels.",
                verbatim_status: "ready",
            },
            products: [
                product({ product_code: "G-CPF-102", name: "Bureautique avancée — Excel & data", modality: "asynchrone", duration_hours: 30, listing_price: 990, funding_price: 990, personal_funding: { type: "rac", montant: 0, exact: true }, eligible_fundings: [{ code: "CPF", label: "CPF", type: "cpf" }], is_top_pick: true, score: 0.6, next_sessions: [] }),
            ]
        })
    ]
};

/** Même structure que `success` mais les verbatims sont en placeholder (Phase B en cours). */
export const mockDegradedPayload: CoachSmartCatalogPayload = {
    ...mockSuccessPayload,
    group_id: "mock-group-degraded",
    status: "degraded",
    trigger_reason: "manual",
    generated_at_ms: NOW - 3 * 1000,
    themes: mockSuccessPayload.themes.map(t => ({
        ...t,
        verbatim: { ...VERBATIM_PENDING },
    }))
};

/** Catalogue réduit : un seul thème santé à deux produits. */
export const mockEmptyPayload: CoachSmartCatalogPayload = {
    type: "coach.smart_catalog",
    group_id: "mock-group-empty",
    catalog_origins: ["sante"],
    trigger_reason: "initial",
    status: "success",
    generated_at_ms: NOW,
    themes: [
        {
            ...mockSuccessPayload.themes[0],
            products: mockSuccessPayload.themes[0].products.slice(0, 2)
        }
    ]
};

export type SmartCatalogMockKey = "success" | "degraded" | "empty";

export const SMART_CATALOG_MOCKS: Record<SmartCatalogMockKey, CoachSmartCatalogPayload> = {
    success: mockSuccessPayload,
    degraded: mockDegradedPayload,
    empty: mockEmptyPayload
};

/**
 * Résout le payload mock à injecter d'après le hash courant — utilisé
 * uniquement en dev. Renvoie `null` quand aucun mock n'est demandé,
 * pour ne pas perturber le flux normal du provider.
 */
export function resolveMockFromHash(hash: string): CoachSmartCatalogPayload | null {
    const match = /#smart-catalog-mock(?:=([a-z]+))?/i.exec(hash);
    if (!match) return null;
    const key = (match[1] || "success").toLowerCase() as SmartCatalogMockKey;
    return SMART_CATALOG_MOCKS[key] ?? mockSuccessPayload;
}
